import { enrichAccountingSnapshot } from './enrichAccountingSnapshot';
import { getCohortSummary, getGenericRoasVectorPct } from './calc/cohortSheetSummary';
import {
  calcBalanceSheetRatio,
  calcCashOpexMultiple,
  calcLiquidValueOpexMultiple,
  calcRunoffOpexRatio,
  calcRunwayFromNetBurn,
  resolveSafeAmount,
} from './calc/underwritingSheetMetrics';

const SNAPSHOT_SOURCE_MANUAL = 'Manual';

function snapshotEntryValue(entry) {
  if (entry == null) {
    return null;
  }
  if (typeof entry !== 'object') {
    return entry;
  }
  if ('value' in entry) {
    return entry.value;
  }
  return entry;
}

function snapshotEntrySource(entry, fallback = '') {
  if (!entry || typeof entry !== 'object') {
    return fallback;
  }
  return entry.source || fallback;
}

function snapshotFieldToAccountingField(entry) {
  const value = snapshotEntryValue(entry);
  if (value == null) {
    return undefined;
  }
  return {
    value,
    reason: (entry && entry.comment) || '',
  };
}

function buildAccountingSnapshotFromFinancialSnapshot(snapshot) {
  if (!snapshot) {
    return null;
  }
  const raw = {
    gpm: snapshotFieldToAccountingField(snapshot.qcfGpm),
    opex: snapshotFieldToAccountingField(snapshot.qcfOpex),
    beginCash: snapshotFieldToAccountingField(snapshot.qcfBeginCash),
    revenueGrowthYoy: snapshotFieldToAccountingField(snapshot.qcfRevenueGrowthYoy),
    totalAssets: snapshotFieldToAccountingField(snapshot.qcfTotalAssets),
    totalLiabilities: snapshotFieldToAccountingField(snapshot.qcfTotalLiabilities),
    safe: snapshotFieldToAccountingField(snapshot.qcfSafe),
    debt: snapshotFieldToAccountingField(snapshot.qcfDebt),
    cashBalance: snapshotFieldToAccountingField(snapshot.qcfCashBalance),
    netBurn: snapshotFieldToAccountingField(snapshot.qcfNetBurn),
    runway: snapshotFieldToAccountingField(snapshot.qcfRunway),
    balanceSheet: snapshotFieldToAccountingField(snapshot.qcfBalanceSheet),
  };
  const hasAny = Object.values(raw).some(Boolean);
  if (!hasAny) {
    return null;
  }
  return enrichAccountingSnapshot(raw);
}

function mapSnapshotSource(source) {
  if (!source || source === SNAPSHOT_SOURCE_MANUAL) {
    return source === SNAPSHOT_SOURCE_MANUAL ? 'Manual entry' : source || 'Default';
  }
  return source;
}

export function hydrateModelFromFinancialSnapshot(prev, financialSnapshot) {
  if (!financialSnapshot) {
    return prev;
  }
  const mergedSnapshot = mergeComputedSnapshotFields(financialSnapshot);
  const next = { ...prev };
  const accountingSnapshot = buildAccountingSnapshotFromFinancialSnapshot(mergedSnapshot);
  if (accountingSnapshot) {
    next.accountingSnapshot = accountingSnapshot;
  }

  const fieldMap = [
    ['qcfGpm', 'gpm', 'gpmSource'],
    ['qcfOpex', 'opex', 'opexSource'],
    ['qcfBeginCash', 'beginCash', 'beginCashSource'],
    ['qcfRevenueGrowthYoy', 'revenueGrowthYoy', 'revenueGrowthYoySource'],
    ['qcfTotalAssets', 'totalAssets', 'totalAssetsSource'],
    ['qcfTotalLiabilities', 'totalLiabilities', 'totalLiabilitiesSource'],
    ['qcfSafe', 'safe', 'safeSource'],
    ['qcfDebt', 'debt', 'debtSource'],
    ['qcfNetBurn', 'netBurn', 'netBurnSource'],
    ['qcfRunway', 'runway', 'runwaySource'],
    ['qcfCashBalance', 'cashBalance', 'cashBalanceSource'],
    ['qcfBalanceSheet', 'balanceSheet', 'balanceSheetSource'],
  ];

  fieldMap.forEach(([snapKey, valueKey, sourceKey]) => {
    const entry = mergedSnapshot[snapKey];
    const value = snapshotEntryValue(entry);
    if (value != null && value !== '') {
      next[valueKey] = value;
      next[sourceKey] = mapSnapshotSource(snapshotEntrySource(entry));
    }
  });

  const cohortSheet = snapshotEntryValue(mergedSnapshot.qcfCohortSheet);
  if (cohortSheet?.cohorts?.length) {
    next.cohortSheet = cohortSheet;
    const roasVector = resolveRoasVectorFromCohortSheet(cohortSheet);
    if (roasVector.length) {
      next.roas = roasVector;
      next.roasSource = 'Cohort Excel (3-mo avg)';
      next.roasMode = 'ai';
    }
  }

  const runoffEntry = mergedSnapshot.qcfRunoff;
  const liquidEntry = mergedSnapshot.qcfLiquidValue;
  if (runoffEntry != null) {
    next.predictedRunoff = snapshotEntryValue(runoffEntry) ?? next.predictedRunoff;
    next.predictedRunoffSource = mapSnapshotSource(snapshotEntrySource(runoffEntry));
  }
  if (liquidEntry != null) {
    next.liquidValue = snapshotEntryValue(liquidEntry);
    next.liquidValueSource = mapSnapshotSource(snapshotEntrySource(liquidEntry));
  }

  return next;
}

export function resolveRoasVectorFromCohortSheet(cohortSheet) {
  if (!cohortSheet?.cohorts?.length) {
    return [];
  }
  const vector = getGenericRoasVectorPct(cohortSheet);
  if (!Array.isArray(vector) || vector.length === 0) {
    return [];
  }
  return vector.map((value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  });
}

function numericSnapshotEntry(entry) {
  const raw = snapshotEntryValue(entry);
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw === 'object') {
    if (raw.amount != null && Number.isFinite(Number(raw.amount))) {
      return Number(raw.amount);
    }
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function isManualSnapshotField(entry) {
  const source = snapshotEntrySource(entry);
  return source === 'Manual' || source === SNAPSHOT_SOURCE_MANUAL;
}

export function computeSnapshotCashBalanceFields(financialSnapshot) {
  const beginCash = numericSnapshotEntry(financialSnapshot?.qcfBeginCash);
  const opex = numericSnapshotEntry(financialSnapshot?.qcfOpex);
  if (beginCash == null || opex == null || opex <= 0) {
    return {};
  }
  const ratio = calcCashOpexMultiple(beginCash, opex);
  if (ratio == null) {
    return {};
  }
  return {
    qcfCashBalance: {
      value: ratio,
      source: 'Calculated',
      comment: 'Beginning cash ÷ monthly OPEX',
    },
  };
}

export function computeSnapshotBalanceSheetFields(financialSnapshot) {
  const assets = numericSnapshotEntry(financialSnapshot?.qcfTotalAssets);
  const liabilities = numericSnapshotEntry(financialSnapshot?.qcfTotalLiabilities);
  if (assets == null || liabilities == null) {
    return {};
  }
  const safe = resolveSafeAmount(snapshotEntryValue(financialSnapshot?.qcfSafe));
  const ratio = calcBalanceSheetRatio(assets, liabilities, safe);
  if (ratio == null) {
    return {};
  }
  return {
    qcfBalanceSheet: {
      value: ratio,
      source: 'Calculated',
      comment: 'Total assets ÷ (total liabilities − SAFE)',
    },
  };
}

export function computeSnapshotRunwayFields(financialSnapshot) {
  const beginCash = numericSnapshotEntry(financialSnapshot?.qcfBeginCash);
  const netBurn = numericSnapshotEntry(financialSnapshot?.qcfNetBurn);
  const months = calcRunwayFromNetBurn(beginCash, netBurn);
  if (months == null) {
    return {};
  }
  return {
    qcfRunway: {
      value: months,
      source: 'Calculated',
      comment: 'Beginning cash ÷ net burn',
    },
  };
}

export function mergeComputedSnapshotFields(financialSnapshot) {
  if (!financialSnapshot) {
    return null;
  }
  return {
    ...financialSnapshot,
    ...computeSnapshotRunoffLiquidFields(financialSnapshot),
    ...computeSnapshotBalanceSheetFields(financialSnapshot),
    ...computeSnapshotCashBalanceFields(financialSnapshot),
    ...computeSnapshotRunwayFields(financialSnapshot),
  };
}

export function computeSnapshotRunoffLiquidFields(financialSnapshot) {
  const cohortSheet = snapshotEntryValue(financialSnapshot?.qcfCohortSheet);
  const opex = snapshotEntryValue(financialSnapshot?.qcfOpex);
  const beginCash = snapshotEntryValue(financialSnapshot?.qcfBeginCash);
  const debt = snapshotEntryValue(financialSnapshot?.qcfDebt);
  if (!cohortSheet?.cohorts?.length || opex == null) {
    return {};
  }
  const summary = getCohortSummary(cohortSheet);
  const runoff12 = summary.runoff12Months;
  const out = {};
  if (!isManualSnapshotField(financialSnapshot?.qcfRunoff)) {
    const runoffRatio = calcRunoffOpexRatio(runoff12, opex);
    if (runoffRatio != null) {
      out.qcfRunoff = { value: runoffRatio };
    }
  }
  if (!isManualSnapshotField(financialSnapshot?.qcfLiquidValue)) {
    const liquidValue = calcLiquidValueOpexMultiple(beginCash, runoff12, debt, opex);
    if (liquidValue != null) {
      out.qcfLiquidValue = { value: liquidValue };
    }
  }
  return out;
}
